// EspaciosCoworking.tsx — Co Marca — ruta: /espacios/coworking
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Wifi, Coffee, Lock, Clock, Users, Zap, Monitor, Printer,
    ChevronRight, ArrowLeft, MapPin, Star, CheckCircle2
} from 'lucide-react';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }

@keyframes blobFloat {
  0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
  50%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes sectionFadeUp {
  from { opacity:0; transform:translateY(22px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
  0%,100% { box-shadow:0 0 0 0 rgba(97, 37, 123,.35); }
  50%      { box-shadow:0 0 0 6px rgba(97, 37, 123,0); }
}
@keyframes rowFadeIn {
  from { opacity:0; transform:translateX(-12px); }
  to   { opacity:1; transform:translateX(0); }
}

.ec-hero-blob { animation: blobFloat 9s ease-in-out infinite; }
.ec-section  { animation: sectionFadeUp .7s cubic-bezier(.16,1,.3,1) both; }
.ec-card {
  transition: all .25s cubic-bezier(.16,1,.3,1);
  border-top: 4px solid transparent;
}
.ec-card:hover {
  transform: translateY(-4px);
  border-top-color: var(--card-accent);
  box-shadow: 0 12px 36px var(--card-shadow) !important;
}
.ec-card:hover .ec-icon-box {
  background: var(--card-accent) !important;
  color: #fff !important;
}
.ec-plan-card { transition: all .25s cubic-bezier(.16,1,.3,1); }
.ec-plan-card:hover { transform: translateY(-3px); }
.ec-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(97, 37, 123,.35) !important; }
.ec-btn-ghost:hover { background: #f1f5f9 !important; }

@media (max-width:900px) {
  .ec-hero-layout { flex-direction: column !important; }
  .ec-hero-visual { display: none !important; }
  .ec-features-grid { grid-template-columns: 1fr 1fr !important; }
  .ec-plans-grid { grid-template-columns: 1fr !important; }
  .ec-cta-row { flex-direction: column !important; align-items: flex-start !important; }
}
@media (max-width:600px) {
  .ec-features-grid { grid-template-columns: 1fr !important; }
  .ec-stats-row { flex-direction: column !important; gap: 10px !important; }
}
`;

// ── Datos ────────────────────────────────────────────────────────────────
const FEATURES = [
    { icon: <Wifi size={20}/>,     label: 'WiFi 300 Mbps',        desc: 'Conexión simétrica dedicada sin interrupciones para videollamadas y trabajo en la nube.', color: colors.secondary },
    { icon: <Coffee size={20}/>,   label: 'Café ilimitado',        desc: 'Disfruta café de especialidad, té y snacks durante toda tu jornada de trabajo.', color: colors.accentDark },
    { icon: <Lock size={20}/>,     label: 'Locker personal',       desc: 'Guarda tus pertenencias con total seguridad en un casillero exclusivo para ti.', color: colors.primary },
    { icon: <Monitor size={20}/>,  label: 'Monitores externos',    desc: 'Amplía tu espacio de trabajo con monitores de 27" disponibles en estaciones fijas.', color: colors.secondaryLight },
    { icon: <Printer size={20}/>,  label: 'Impresión incluida',    desc: '50 páginas de impresión mensuales incluidas en tu membresía sin costo extra.', color: colors.accentDark },
    { icon: <Clock size={20}/>,    label: 'Horario extendido',     desc: 'Accede a nuestras instalaciones de lunes a sábado de 7:00 am a 10:00 pm.', color: colors.secondary },
    { icon: <Users size={20}/>,    label: 'Comunidad activa',      desc: 'Únete a eventos mensuales de networking, talleres y charlas con líderes del sector.', color: colors.primary },
    { icon: <Zap size={20}/>,      label: 'Setup inmediato',       desc: 'Llega y trabaja desde el primer día. Sin trámites, sin esperas, sin contratos largos.', color: colors.secondaryLight },
];

const PLANS = [
    {
        name: 'Day Pass',
        price: '45',
        unit: 'día',
        desc: 'Perfecto para probar el espacio o visitas ocasionales sin compromiso.',
        features: ['1 día completo de acceso', 'WiFi + café', 'Locker temporal', 'Acceso áreas comunes'],
        color: colors.secondary,
        badge: null,
    },
    {
        name: 'Membresía Mensual',
        price: '350',
        unit: 'mes',
        desc: 'La opción favorita de freelancers y profesionales independientes en La Paz.',
        features: ['Acceso ilimitado L–V', 'WiFi + café ilimitado', 'Locker permanente', '4 horas sala reuniones/mes', 'Comunidad + eventos'],
        color: colors.primary,
        badge: 'Más popular',
    },
    {
        name: 'Equipo (5 pax)',
        price: '1,400',
        unit: 'mes',
        desc: 'Para equipos pequeños que buscan un espacio compartido con beneficios extra.',
        features: ['5 membresías mensuales', 'Sala reuniones 8 hrs/mes', 'Armario de equipo', 'Gestión centralizada', 'Soporte prioritario'],
        color: colors.secondaryLight,
        badge: 'Ahorra 20%',
    },
];

const STATS = [
    { value: '50+', label: 'Puestos disponibles', color: colors.secondary },
    { value: '7am', label: 'Apertura diaria', color: colors.primary },
    { value: '300', label: 'Mbps WiFi', color: colors.accentDark },
    { value: '5★',  label: 'Valoración promedio', color: colors.secondaryLight },
];

const TESTIMONIALS = [
    { name: 'Valeria M.', role: 'Diseñadora UX Freelance', text: 'Co Marca cambió mi forma de trabajar. La comunidad es increíble y la conexión nunca falla. Lo recomiendo a cualquier profesional independiente.', avatar: 'VM', color: colors.secondary },
    { name: 'Rodrigo S.', role: 'Desarrollador Full-Stack', text: 'Vengo desde hace 8 meses y no cambiaría el espacio por nada. Los monitores externos y el café de especialidad son un plus enorme para mi productividad.', avatar: 'RS', color: colors.primary },
    { name: 'Ana K.', role: 'Consultora de Marketing', text: 'El ambiente es profesional y la energía del lugar te contagia. He conocido clientes aquí que ahora son parte de mi red permanente.', avatar: 'AK', color: colors.accentDark },
];

// ── MAIN ────────────────────────────────────────────────────────────────
const EspaciosCoworking: React.FC = () => {
    const navigate = useNavigate();
    const [heroVisible, setHeroVisible] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setHeroVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    return (
        <div style={{ fontFamily: "'DM Sans',sans-serif", background: '#fff', overflowX: 'hidden' }}>
            <style>{css}</style>

            {/* ══ HERO ══ */}
            <section style={{
                minHeight: '88vh',
                background: 'linear-gradient(135deg, #fff 0%, #f0f8ff 50%, #e0f3ff 100%)',
                display: 'flex', alignItems: 'center',
                position: 'relative', overflow: 'hidden',
                paddingTop: 'clamp(92px, 12vw, 115px)',
            }}>
                {/* Blob */}
                <div className="ec-hero-blob" style={{
                    position: 'absolute', top: '-100px', right: '-100px',
                    width: '520px', height: '520px',
                    background: `linear-gradient(135deg, ${colors.secondary}22, ${colors.secondaryLight}18)`,
                    pointerEvents: 'none',
                }} />
                {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    opacity={0.65}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <div style={{ maxWidth:'1180px', margin:'0 auto', padding:'0 clamp(20px,4vw,40px)', width:'100%' }}>
                    <div className="ec-hero-layout" style={{ display:'flex', alignItems:'center', gap:'64px' }}>

                        {/* LEFT */}
                        <div style={{ flex:1, maxWidth:'560px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
                            transition: 'opacity .9s ease, transform .9s cubic-bezier(.16,1,.3,1)',
                        }}>
                            {/* Back */}
                            <button onClick={() => navigate(-1)} style={{
                                display:'inline-flex', alignItems:'center', gap:'6px',
                                background:'transparent', border:'none', cursor:'pointer',
                                color:'#94a3b8', fontSize:'.8rem', fontWeight:'600',
                                fontFamily:"'DM Sans',sans-serif", marginBottom:'24px', padding:0,
                            }}
                                onMouseEnter={e => e.currentTarget.style.color = colors.secondary}
                                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                            >
                                <ArrowLeft size={14}/> Volver
                            </button>

                            {/* Breadcrumb */}
                            <nav style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'18px' }}>
                                <a href="/" style={{ color:'#94a3b8', textDecoration:'none' }}
                                    onMouseEnter={e=>e.currentTarget.style.color=colors.secondary}
                                    onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>Inicio</a>
                                <ChevronRight size={10} color="#d1d5db"/>
                                <span style={{ color: colors.secondary, fontWeight:'700' }}>Coworking</span>
                            </nav>

                            {/* Badge */}
                            <div style={{
                                display:'inline-flex', alignItems:'center', gap:'8px',
                                background:`${colors.secondary}12`, border:`1.5px solid ${colors.secondary}28`,
                                borderRadius:'100px', padding:'6px 16px', marginBottom:'20px',
                                animation: 'pulseD 2.5s infinite',
                            }}>
                                <span style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors.secondary, display:'inline-block' }}/>
                                <span style={{ fontSize:'.76rem', fontWeight:'700', color: colors.secondary, letterSpacing:'.08em', textTransform:'uppercase' }}>
                                    Espacio de Coworking
                                </span>
                            </div>

                            <h1 style={{
                                fontSize:'clamp(2.4rem,5vw,3.6rem)', fontWeight:'900',
                                color:'#0f172a', lineHeight:1.05, letterSpacing:'-.025em', marginBottom:'22px',
                            }}>
                                Tu espacio de trabajo,{' '}
                                <span style={{ color: colors.secondary }}>sin complicaciones</span>
                            </h1>

                            <p style={{ fontSize:'1.05rem', color:'#475569', lineHeight:1.85, marginBottom:'32px', maxWidth:'480px' }}>
                                Membresías flexibles, comunidad activa y todo lo que necesitás para trabajar a tu mejor nivel. Desde un día hasta todo el mes, sin contratos largos ni letra chica.
                            </p>

                            {/* Stats en fila */}
                            <div className="ec-stats-row" style={{ display:'flex', gap:'16px', marginBottom:'36px', flexWrap:'wrap' }}>
                                {STATS.map((s, i) => (
                                    <React.Fragment key={s.label}>
                                        <div style={{ textAlign:'center' }}>
                                            <div style={{ fontSize:'1.6rem', fontWeight:'900', color: s.color, lineHeight:1 }}>{s.value}</div>
                                            <div style={{ fontSize:'.7rem', color:'#94a3b8', fontWeight:'600', marginTop:'2px' }}>{s.label}</div>
                                        </div>
                                        {i < STATS.length - 1 && (
                                            <div style={{ width:'1px', background:'#f1f5f9', alignSelf:'stretch' }}/>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Barra colores */}
                            <div style={{ display:'flex', gap:'5px', marginBottom:'28px' }}>
                                {[colors.secondary, colors.primary, colors.accentDark, colors.secondaryLight].map((c,i) => (
                                    <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>

                            {/* CTAs */}
                            <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                                <a href="/reservaya" className="ec-btn-primary" style={{
                                    display:'inline-flex', alignItems:'center', gap:'8px',
                                    background:`linear-gradient(135deg, ${colors.secondary}, #005fa3)`,
                                    color:'#fff', borderRadius:'13px', padding:'13px 28px',
                                    fontWeight:'800', fontSize:'.95rem', textDecoration:'none',
                                    boxShadow:`0 6px 20px ${colors.secondary}35`, transition:'all .22s',
                                    fontFamily:"'DM Sans',sans-serif",
                                }}>
                                    Reservar ahora →
                                </a>
                                <a href="/contacto" className="ec-btn-ghost" style={{
                                    display:'inline-flex', alignItems:'center', gap:'8px',
                                    background:'#f8fafc', border:`1.5px solid #e2e8f0`,
                                    color:'#374151', borderRadius:'13px', padding:'13px 24px',
                                    fontWeight:'700', fontSize:'.95rem', textDecoration:'none', transition:'all .22s',
                                }}>
                                    Hablar con un asesor
                                </a>
                            </div>
                        </div>

                        {/* RIGHT — visual */}
                        <div className="ec-hero-visual" style={{
                            flex:1, maxWidth:'500px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateX(0)' : 'translateX(40px)',
                            transition: 'opacity 1s ease .15s, transform 1s cubic-bezier(.16,1,.3,1) .15s',
                        }}>
                            {/* Imagen principal */}
                            <div style={{
                                width:'100%', aspectRatio:'4/3', borderRadius:'28px',
                                background:`linear-gradient(145deg, #004d7a, ${colors.secondary} 50%, ${colors.secondaryLight})`,
                                display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'12px',
                                boxShadow:`0 28px 72px ${colors.secondary}28, 0 8px 24px rgba(0,0,0,.08)`,
                                position:'relative', overflow:'hidden',
                            }}>
                                <div style={{ position:'absolute', top:'-40px', right:'-40px', width:'220px', height:'220px', background:'rgba(255,255,255,.08)', borderRadius:'50%' }}/>
                                <div style={{ position:'absolute', bottom:'-60px', left:'-40px', width:'260px', height:'260px', background:'rgba(255,255,255,.05)', borderRadius:'50%' }}/>
                                <div style={{ fontSize:'5rem', filter:'drop-shadow(0 4px 20px rgba(0,0,0,.15))' }}>🧑‍💻</div>
                                <div style={{ background:'rgba(255,255,255,.18)', backdropFilter:'blur(12px)', borderRadius:'14px', padding:'10px 22px', border:'1px solid rgba(255,255,255,.28)', color:'#fff', fontWeight:'700', fontSize:'.9rem' }}>
                                    Cowork · La Paz
                                </div>
                                {/* Badge flotante */}
                                <div style={{
                                    position:'absolute', bottom:'20px', right:'-20px',
                                    background:'#fff', borderRadius:'16px', padding:'12px 18px',
                                    boxShadow:'0 12px 32px rgba(0,0,0,.1)',
                                    display:'flex', alignItems:'center', gap:'10px',
                                }}>
                                    <Star size={16} color={colors.accentDark} fill={colors.accentDark}/>
                                    <div>
                                        <div style={{ fontWeight:'800', fontSize:'.95rem', color:'#0f172a' }}>4.9 / 5</div>
                                        <div style={{ fontSize:'.68rem', color:'#94a3b8' }}>+120 reseñas</div>
                                    </div>
                                </div>
                                {/* Badge top izq */}
                                <div style={{
                                    position:'absolute', top:'20px', left:'-18px',
                                    background:'#fff', borderRadius:'14px', padding:'10px 16px',
                                    boxShadow:'0 8px 24px rgba(0,0,0,.09)',
                                    display:'flex', alignItems:'center', gap:'8px',
                                }}>
                                    <MapPin size={14} color={colors.primary}/>
                                    <div style={{ fontSize:'.75rem', fontWeight:'700', color:'#0f172a' }}>Calacoto, La Paz</div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ══ FEATURES ══ */}
            <section className="ec-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'52px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.secondary, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.secondary }}>
                                Lo que incluye
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.secondary, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em', lineHeight:1.1 }}>
                            Todo lo que necesitás,{' '}
                            <span style={{ color: colors.secondary }}>incluido</span>
                        </h2>
                    </div>

                    <div className="ec-features-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                        {FEATURES.map((f, i) => (
                            <div key={f.label} className="ec-card" style={{
                                '--card-accent': f.color,
                                '--card-shadow': `${f.color}18`,
                                background:'#fff', borderRadius:'18px', padding:'24px',
                                boxShadow:'0 2px 12px rgba(0,0,0,.04)',
                                animationDelay: `${i * .06}s`,
                            } as React.CSSProperties}>
                                <div className="ec-icon-box" style={{
                                    width:'44px', height:'44px', borderRadius:'12px',
                                    background:`${f.color}12`, color: f.color,
                                    display:'flex', alignItems:'center', justifyContent:'center',
                                    marginBottom:'14px', transition:'all .22s',
                                }}>
                                    {f.icon}
                                </div>
                                <h3 style={{ fontSize:'.9rem', fontWeight:'800', color:'#0f172a', marginBottom:'6px' }}>{f.label}</h3>
                                <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.6 }}>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ PLANES ══ */}
            <section className="ec-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'52px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.primary, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.primary }}>
                                Planes y precios
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.primary, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>
                            Elige el plan que{' '}
                            <span style={{ color: colors.primary }}>se ajusta a ti</span>
                        </h2>
                        <p style={{ fontSize:'.95rem', color:'#64748b', marginTop:'10px' }}>Sin contratos a largo plazo. Cancela cuando quieras.</p>
                    </div>

                    <div className="ec-plans-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px' }}>
                        {PLANS.map((plan) => {
                            const isPopular = !!plan.badge && plan.badge === 'Más popular';
                            return (
                                <div key={plan.name} className="ec-plan-card" style={{
                                    background: isPopular ? `linear-gradient(145deg, ${colors.primary}08, #fff)` : '#fff',
                                    borderRadius:'22px',
                                    border: isPopular ? `2px solid ${colors.primary}40` : '1.5px solid #f1f5f9',
                                    padding:'28px 24px',
                                    boxShadow: isPopular ? `0 8px 32px ${colors.primary}14` : '0 2px 12px rgba(0,0,0,.04)',
                                    position:'relative',
                                }}>
                                    {plan.badge && (
                                        <span style={{
                                            position:'absolute', top:'-11px', left:'50%', transform:'translateX(-50%)',
                                            background: isPopular ? `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` : `linear-gradient(135deg, ${colors.secondaryLight}, ${colors.secondary})`,
                                            color:'#fff', fontSize:'.65rem', fontWeight:'800',
                                            letterSpacing:'.08em', textTransform:'uppercase',
                                            borderRadius:'100px', padding:'3px 14px',
                                            whiteSpace:'nowrap',
                                        }}>{plan.badge}</span>
                                    )}
                                    {/* Header plan */}
                                    <div style={{ marginBottom:'20px' }}>
                                        <div style={{ fontSize:'.65rem', fontWeight:'700', color: plan.color, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:'6px' }}>
                                            {plan.name}
                                        </div>
                                        <div style={{ display:'flex', alignItems:'baseline', gap:'4px', marginBottom:'8px' }}>
                                            <span style={{ fontSize:'2.2rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.03em' }}>{plan.price}</span>
                                            <span style={{ fontSize:'.75rem', color:'#94a3b8', fontWeight:'600' }}>Bs./{plan.unit}</span>
                                        </div>
                                        <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.55 }}>{plan.desc}</p>
                                    </div>
                                    {/* Divider */}
                                    <div style={{ height:'1.5px', background:'#f1f5f9', marginBottom:'18px' }}/>
                                    {/* Features */}
                                    <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:'9px', marginBottom:'22px' }}>
                                        {plan.features.map(f => (
                                            <li key={f} style={{ display:'flex', alignItems:'center', gap:'9px', fontSize:'.82rem', color:'#374151', fontWeight:'500' }}>
                                                <CheckCircle2 size={14} color={plan.color} style={{ flexShrink:0 }}/>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a href="/reservaya" style={{
                                        display:'block', textAlign:'center',
                                        background: isPopular ? `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` : `${plan.color}14`,
                                        color: isPopular ? '#fff' : plan.color,
                                        borderRadius:'11px', padding:'11px',
                                        fontWeight:'800', fontSize:'.88rem', textDecoration:'none',
                                        boxShadow: isPopular ? `0 4px 14px ${colors.primary}28` : 'none',
                                        transition:'all .2s',
                                    }}
                                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        {isPopular ? 'Empezar ahora →' : 'Seleccionar plan'}
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ══ TESTIMONIOS ══ */}
            <section className="ec-section" style={{ background:'#fff', padding:'80px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'1180px', margin:'0 auto' }}>
                    <div style={{ textAlign:'center', marginBottom:'48px' }}>
                        <div style={{ display:'inline-flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
                            <div style={{ height:'2px', width:'36px', background: colors.accentDark, borderRadius:'2px' }}/>
                            <span style={{ fontSize:'.75rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color: colors.accentDark }}>
                                Testimonios
                            </span>
                            <div style={{ height:'2px', width:'36px', background: colors.accentDark, borderRadius:'2px' }}/>
                        </div>
                        <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.4rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>
                            Lo que dicen nuestros{' '}
                            <span style={{ color: colors.accentDark }}>miembros</span>
                        </h2>
                    </div>

                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px,1fr))', gap:'20px' }}>
                        {TESTIMONIALS.map(t => (
                            <div key={t.name} style={{
                                background:'#fff', borderRadius:'20px', padding:'28px 24px',
                                boxShadow:'0 2px 16px rgba(0,0,0,.05)',
                                border:'1.5px solid #f1f5f9',
                                borderTop:`4px solid ${t.color}`,
                            }}>
                                <div style={{ display:'flex', gap:'14px', alignItems:'center', marginBottom:'16px' }}>
                                    <div style={{
                                        width:'44px', height:'44px', borderRadius:'50%',
                                        background:`linear-gradient(135deg, ${t.color}, ${t.color}88)`,
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        color:'#fff', fontWeight:'800', fontSize:'.85rem', flexShrink:0,
                                    }}>{t.avatar}</div>
                                    <div>
                                        <div style={{ fontWeight:'800', fontSize:'.9rem', color:'#0f172a' }}>{t.name}</div>
                                        <div style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>{t.role}</div>
                                    </div>
                                    <div style={{ marginLeft:'auto', display:'flex', gap:'2px' }}>
                                        {[...Array(5)].map((_,i) => <Star key={i} size={11} color={colors.accentDark} fill={colors.accentDark}/>)}
                                    </div>
                                </div>
                                <p style={{ fontSize:'.85rem', color:'#475569', lineHeight:1.7, fontStyle:'italic' }}>
                                    "{t.text}"
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ CTA FINAL ══ */}
            <section className="ec-section" style={{ background:`linear-gradient(135deg, #004d7a, ${colors.secondary} 60%, ${colors.secondaryLight})`, padding:'72px clamp(20px,4vw,40px)' }}>
                <div style={{ maxWidth:'800px', margin:'0 auto', textAlign:'center' }}>
                    <div style={{ fontSize:'2.8rem', marginBottom:'16px' }}>🚀</div>
                    <h2 style={{ fontSize:'clamp(1.8rem,3vw,2.6rem)', fontWeight:'900', color:'#fff', letterSpacing:'-.025em', marginBottom:'14px' }}>
                        ¿Listo para empezar?
                    </h2>
                    <p style={{ fontSize:'1.05rem', color:'rgba(255,255,255,.82)', lineHeight:1.75, marginBottom:'32px', maxWidth:'500px', margin:'0 auto 32px' }}>
                        Reserva tu primer día gratis y descubrí por qué más de 300 profesionales eligen Co Marca para trabajar.
                    </p>
                    <div className="ec-cta-row" style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
                        <a href="/reservaya" style={{
                            display:'inline-flex', alignItems:'center', gap:'8px',
                            background:'#fff', color: colors.secondary,
                            borderRadius:'13px', padding:'14px 30px',
                            fontWeight:'900', fontSize:'1rem', textDecoration:'none',
                            boxShadow:'0 6px 20px rgba(0,0,0,.15)',
                            transition:'all .22s',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            Reservar mi espacio →
                        </a>
                        <a href="/contacto" style={{
                            display:'inline-flex', alignItems:'center', gap:'8px',
                            background:'rgba(255,255,255,.15)', backdropFilter:'blur(8px)',
                            border:'1.5px solid rgba(255,255,255,.35)', color:'#fff',
                            borderRadius:'13px', padding:'14px 26px',
                            fontWeight:'700', fontSize:'.95rem', textDecoration:'none',
                            transition:'all .22s',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.25)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.15)'; }}
                        >
                            Contactar equipo
                        </a>
                    </div>

                    {/* Barra colores */}
                    <div style={{ display:'flex', gap:'5px', justifyContent:'center', marginTop:'40px' }}>
                        {[colors.secondary, colors.primary, colors.accentDark, colors.secondaryLight].map((c,i) => (
                            <div key={i} style={{ height:'3px', width:i===0?'28px':'10px', borderRadius:'3px', background:'rgba(255,255,255,.5)' }}/>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};

export default EspaciosCoworking;